import React, { Component } from "react";
import ScrollToTop from "../../components/utils/ScrollToTop";
import Card from "../../components/Card";
import CheckList, { CheckListItem } from "../../components/CheckList";
import Action from "../../components/Action";
import List, { ListItem } from "../../components/List";
import ReferralCodeBanner from "../../components/ReferralCodeBanner";
import { formatMoneyWithCurrency, moneyStrToNum } from "../../shared/utils/moneyUtils";
import Image from "../../components/Image";
import imageRelaxingOnSofa from "../../images/relaxing-on-sofa.png";
import {
  ActionWrapper,
  ListItemSuffix,
  QuoteBreakdown,
  QuotedPrice,
  QuotedPriceWrapper,
} from "../QuoteCommon/QuoteStyles";

class BrokerQuoteDetailsPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      quoteDetails: null,
      loading: true,
      error: null,
    };
  }

  handleFetchError = (err) => {
    console.error("Quote generation failed:", err);
    this.setState({ error: "Unable to generate your quote", loading: false });
  };

  componentDidMount() {
    if (this.hasInitiated) return;
    this.hasInitiated = true;

    fetch("/api/partialQuote", {
      method: "PUT",
      body: JSON.stringify({
        channel: this.props.channel,
        sellingDetails:
          this.props.transactionType === "Selling" || this.props.transactionType === "BuyingAndSelling"
            ? this.props.sellingDetails
            : null,
        buyingDetails:
          this.props.transactionType === "Buying" || this.props.transactionType === "BuyingAndSelling"
            ? this.props.buyingDetails
            : null,
        remortgagingDetails: null,
        referralCode: this.props.referral ? this.props.referral.referralCode : null,
        googleAnalyticsId: this.props.googleAnalyticsId,
        campaign: this.props.campaign ?? null,
        agentName: this.props.broker.fullName,
        agentEmailAddress: this.props.broker.emailAddress,
        additionalInformation: this.props.additionalInformation,
      }),
    })
      .then(async (resp) => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const quoteData = await resp.json();
        this.setState({ quoteDetails: quoteData, loading: false });
      }, this.handleFetchError)
      .catch(this.handleFetchError);
  }

  continueSendClientQuote = () => {
    this.props.onStepComplete({
      progress: "sendClientQuote",
      quoteDetails: this.state.quoteDetails,
    });
  };

  render() {
    if (!!this.state.loading) {
      return null;
    }

    if (this.state.error) {
      return (
        <React.Fragment>
          <ScrollToTop />
          <Card title="Sorry, that didn’t work" removeTopPadding>
            {this.state.error}
          </Card>
        </React.Fragment>
      );
    }

    const transactionType = this.props.transactionType;

    let saleLegalFee = "";
    let salePropertyPrice = "";

    let purchaseLegalFee = "";
    let purchasePropertyPrice = "";

    let quotePrice = "";

    let stampDuty = "";
    let stampDutyDescription = "Stamp Duty";

    let landRegistryFee = "";
    let companiesHouseRegistrationFee = "";
    let totalFeesAndTaxes = "";

    const formatQuotedAmount = (value, showDecimal) => {
      if (!value) return "";
      return formatMoneyWithCurrency(moneyStrToNum(value).toString(), showDecimal);
    };

    let includeDecimal = landRegistryFee.includes(".") || stampDuty.includes(".");

    const buyingDetails = this.props.buyingDetails;
    const sellingDetails = this.props.sellingDetails;

    const { quoteDetails } = this.state;
    if (quoteDetails) {
      quotePrice = formatQuotedAmount(quoteDetails.totalPrice, includeDecimal);
      saleLegalFee = formatQuotedAmount(quoteDetails.sellingQuotePrice, includeDecimal);
      purchaseLegalFee = formatQuotedAmount(quoteDetails.buyingQuotePrice, includeDecimal);
      stampDuty = formatQuotedAmount(quoteDetails.stampDutyFee, includeDecimal);
      landRegistryFee = formatQuotedAmount(quoteDetails.landRegistryFee, includeDecimal);
      companiesHouseRegistrationFee = formatQuotedAmount(quoteDetails.companiesHouseRegistrationFee, includeDecimal);
      totalFeesAndTaxes = formatQuotedAmount(quoteDetails.totalFeesAndTaxes, includeDecimal);

      // First-time buyer relief is not available for buy-to-let properties
      if (buyingDetails && !!buyingDetails.firstTimeBuyer && !!buyingDetails.buyToLet) {
        stampDutyDescription = "Stamp Duty (no first-time buyer relief)";
      }

      purchasePropertyPrice =
        transactionType === "Buying" || transactionType === "BuyingAndSelling"
          ? formatQuotedAmount(buyingDetails?.price, includeDecimal)
          : "";

      salePropertyPrice =
        transactionType === "Selling" || transactionType === "BuyingAndSelling"
          ? formatQuotedAmount(sellingDetails?.price, includeDecimal)
          : "";
    }

    function getTenure(details) {
      if (details?.tenure === "leasehold with share of freehold") {
        return "share of freehold";
      }
      return details.tenure;
    }

    let sellingTenure = "";
    let buyingTenure = "";

    if (transactionType === "Selling" || transactionType === "BuyingAndSelling") {
      sellingTenure = getTenure(sellingDetails);
    }

    if (transactionType === "Buying" || transactionType === "BuyingAndSelling") {
      buyingTenure = getTenure(buyingDetails);
    }

    const transactionTypes = {
      Buying: {
        typeOfPurchaseIncTenure: `${buyingTenure} purchase`,
        quotePriceText: `That’s our quote, including VAT, for legal work on a ${buyingTenure} purchase costing ${purchasePropertyPrice}.`,
      },
      BuyingAndSelling: {
        typeOfPurchaseIncTenure: `${buyingTenure} purchase and ${sellingTenure} sale`,
        quotePriceText: `That’s our quote, including VAT, for legal work on a ${buyingTenure} purchase costing ${purchasePropertyPrice} and ${sellingTenure} sale costing ${salePropertyPrice}.`,
      },
    };

    const { typeOfPurchaseIncTenure, quotePriceText } = transactionTypes[transactionType] || {};

    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title={`Conveyancing quote for ${this.props.personalDetails.firstName}`} removeTopPadding>
          <QuotedPriceWrapper>
            <QuotedPrice>{quotePrice}</QuotedPrice>
            <p>{quotePriceText}</p>

            <CheckList>
              <CheckListItem>Simpler, clearer, faster</CheckListItem>
              {transactionType === "BuyingAndSelling" && <CheckListItem>No sale, no fee</CheckListItem>}
              {(transactionType === "BuyingAndSelling" || transactionType === "Buying") && (
                <CheckListItem>Second chance guarantee</CheckListItem>
              )}
              <CheckListItem>No hidden fees</CheckListItem>
            </CheckList>

            <p>
              Our quote includes searches, ID checks and common disbursements.{" "}
              <a
                href="https://www.juno.legal/find-out-more/whats-included-in-our-fixed-fee/"
                target="_blank"
                rel="noreferrer"
              >
                See what’s included in our fixed fee
              </a>
              .
            </p>
          </QuotedPriceWrapper>

          {this.props.referral && <ReferralCodeBanner text={this.props.referral.quoteMessage} withinQuote={true} />}

          {(transactionType === "Buying" || transactionType === "BuyingAndSelling") && (
            <QuoteBreakdown>
              <List>
                {transactionType === "BuyingAndSelling" && (
                  <>
                    <ListItem divider>
                      Legal work on the {sellingTenure} sale
                      <ListItemSuffix>{saleLegalFee}</ListItemSuffix>
                    </ListItem>

                    <ListItem divider>
                      Legal work on the {buyingTenure} purchase
                      <ListItemSuffix>{purchaseLegalFee}</ListItemSuffix>
                    </ListItem>
                  </>
                )}

                {transactionType === "Buying" && (
                  <ListItem divider>
                    Legal fee on the {buyingTenure} purchase
                    <ListItemSuffix data-testid="purchase-legal-fee">{purchaseLegalFee}</ListItemSuffix>
                  </ListItem>
                )}

                <ListItem divider>
                  Land Registry fee
                  <ListItemSuffix data-testid="land-registry-fee">{landRegistryFee}</ListItemSuffix>
                </ListItem>

                <ListItem divider>
                  {stampDutyDescription}
                  <ListItemSuffix data-testid="stamp-duty-fee">{stampDuty}</ListItemSuffix>
                </ListItem>

                {buyingDetails?.clientIsCompany && (
                  <ListItem divider>
                    Companies house registration fee
                    <ListItemSuffix data-testid="companies-house-registration-fee">
                      {companiesHouseRegistrationFee}
                    </ListItemSuffix>
                  </ListItem>
                )}

                <ListItem divider>
                  Total including government fees and taxes
                  <ListItemSuffix data-testid="total-fees-and-taxes">{totalFeesAndTaxes}</ListItemSuffix>
                </ListItem>
              </List>
            </QuoteBreakdown>
          )}

          <ActionWrapper>
            <Action onClick={this.continueSendClientQuote}>Send your client a quote</Action>
          </ActionWrapper>
        </Card>

        {!!buyingDetails && (
          <Card title="Second chance guarantee" headingLevel="3" removeTopPadding>
            <p>
              If your client’s first purchase falls through – for any reason – they can try again for no extra charge.
              <a
                href="https://www.juno.legal/find-out-more/our-guarantees/#our-second-chance-guarantee-for-buyers"
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                Here’s more information
              </a>
              .
            </p>
          </Card>
        )}

        <Image src={imageRelaxingOnSofa} fullWidth />
      </React.Fragment>
    );
  }
}

export default BrokerQuoteDetailsPage;
